import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EuStars } from "../components/EuStars";
import SiteFooter from "../components/SiteFooter";
import {
    ArrowLeft,
    ArrowRight,
    Clock,
    Download,
    Loader2,
    Newspaper,
    Search as SearchIcon,
    Sparkles,
    Tag as TagIcon,
    ExternalLink,
    X,
} from "lucide-react";

function usePublicMode() {
    const location = useLocation();
    return !location.pathname.startsWith("/app");
}

function useBulletinBase() {
    return usePublicMode() ? "/bulletins" : "/app/bulletins";
}

export default function Bulletins() {
    const { slug } = useParams();
    const inner = slug ? <BulletinDetail slug={slug} /> : <BulletinIndex />;
    return <MaybePublicShell>{inner}</MaybePublicShell>;
}

function MaybePublicShell({ children }) {
    const isPublic = usePublicMode();
    const { user } = useAuth();
    if (!isPublic) return children;
    return (
        <div className="min-h-screen flex flex-col bg-[#F5F0E8] text-[#0D1B3E]">
            <header className="w-full">
                <div className="container-edit flex items-center justify-between py-6">
                    <Link to="/" className="flex items-center gap-3" data-testid="bulletins-brand">
                        <EuStars size={32} />
                        <span className="font-display text-xl tracking-tight">EuroLaw Hub</span>
                    </Link>
                    <nav className="flex items-center gap-4">
                        <Link to="/pricing" className="text-sm hover:text-[#C9A84C]">Pricing</Link>
                        {user ? (
                            <Link to="/app" className="btn-primary">Open workspace</Link>
                        ) : (
                            <Link to="/signup" className="btn-primary">Get started</Link>
                        )}
                    </nav>
                </div>
            </header>
            <main className="flex-1">{children}</main>
            <SiteFooter />
        </div>
    );
}

function BulletinIndex() {
    const { user } = useAuth();
    const base = useBulletinBase();
    const [items, setItems] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("");
    const [q, setQ] = useState("");
    const [showGenerate, setShowGenerate] = useState(false);

    const load = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        if (q) params.set("q", q);
        try {
            const { data } = await api.get(`/bulletins?${params.toString()}`);
            setItems(data.items || []);
            setCategories(data.categories || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    const featured = items[0];
    const rest = items.slice(1);

    return (
        <div className="container-edit py-10 lg:py-14" data-testid="bulletins-page">
            {/* SEO meta hint via document.title, set in effect */}
            <PageTitle title="Bulletins | EuroLaw Hub editorial desk" />

            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="label-eyebrow">Library</div>
                    <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                        Bulletins
                    </h1>
                    <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                        Legal insight from EuroLaw Hub&apos;s editorial desk:
                        EU regulation, case law, supervisory practice. Source-grounded.
                        Built for senior practitioners.
                    </p>
                </div>
                <button
                    type="button"
                    className="btn-primary"
                    onClick={() => (user ? setShowGenerate(true) : (window.location.href = `/login?next=${encodeURIComponent(base)}`))}
                    data-testid="bulletins-generate-btn"
                >
                    <Sparkles size={16} /> Generate bulletin
                </button>
            </div>

            <div className="mt-9 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap" data-testid="bulletins-category-filter">
                    <button
                        type="button"
                        onClick={() => setCategory("")}
                        className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                            category === ""
                                ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                        }`}
                        data-testid="bulletins-cat-all"
                    >
                        All
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.key}
                            type="button"
                            onClick={() => setCategory(c.key)}
                            className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 border transition-colors ${
                                category === c.key
                                    ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                    : "bg-transparent text-[#0D1B3E]/75 border-[#0D1B3E]/25 hover:border-[#0D1B3E]"
                            }`}
                            data-testid={`bulletins-cat-${c.key}`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        load();
                    }}
                    className="flex items-center gap-2 min-w-[260px]"
                >
                    <div className="relative flex-1">
                        <SearchIcon
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50"
                        />
                        <input
                            className="field pl-9"
                            placeholder="Search bulletins…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            data-testid="bulletins-search-input"
                        />
                    </div>
                </form>
            </div>

            {loading ? (
                <div className="mt-12 flex items-center gap-3 text-[#0D1B3E]/60">
                    <Loader2 className="animate-spin" size={18} />
                    <span className="label-eyebrow">Loading bulletins…</span>
                </div>
            ) : items.length === 0 ? (
                <div className="mt-12 card-flat p-12 text-center" data-testid="bulletins-empty">
                    <Newspaper size={28} color="#C9A84C" className="mx-auto" />
                    <div className="mt-4 font-display text-2xl tracking-tight">
                        No bulletins match
                    </div>
                    <p className="mt-3 max-w-md mx-auto text-sm text-[#0D1B3E]/70">
                        Try a different category or generate a new bulletin on a topic
                        you care about.
                    </p>
                </div>
            ) : (
                <div className="mt-10 space-y-12" data-testid="bulletins-list">
                    {featured && <FeaturedCard item={featured} base={base} />}
                    {rest.length > 0 && (
                        <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3">
                            {rest.map((b) => (
                                <BulletinCard key={b.id} item={b} base={base} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {showGenerate && (
                <GenerateModal
                    categories={categories}
                    defaultCategory={category}
                    onClose={() => setShowGenerate(false)}
                    onComplete={(slug) => {
                        setShowGenerate(false);
                        if (slug) window.location.assign(`${base}/${slug}`);
                        else load();
                    }}
                />
            )}
        </div>
    );
}

function PageTitle({ title }) {
    useEffect(() => {
        const prev = document.title;
        document.title = title;
        return () => {
            document.title = prev;
        };
    }, [title]);
    return null;
}

function FeaturedCard({ item, base }) {
    return (
        <article
            className="card-flat p-8 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start"
            data-testid={`bulletin-featured-${item.slug}`}
        >
            <div>
                <div className="flex items-center gap-3 label-eyebrow">
                    <span style={{ color: "#C9A84C" }}>Featured</span>
                    {item.category && <span>· {item.category}</span>}
                    <span className="flex items-center gap-1">
                        <Clock size={11} /> {item.reading_minutes} min
                    </span>
                </div>
                <h2 className="font-display text-3xl lg:text-4xl mt-3 tracking-tight leading-[1.15]">
                    <Link to={`${base}/${item.slug}`} className="hover:text-[#0D1B3E]">
                        {item.title}
                    </Link>
                </h2>
                {item.hero_excerpt && (
                    <p className="mt-4 text-[#0D1B3E]/80 leading-relaxed">
                        {item.hero_excerpt}
                    </p>
                )}
                <div className="mt-6 flex items-center gap-2 flex-wrap">
                    {(item.related_tags || []).slice(0, 5).map((t) => (
                        <span
                            key={t}
                            className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                        >
                            #{t}
                        </span>
                    ))}
                </div>
                <div className="mt-7">
                    <Link
                        to={`${base}/${item.slug}`}
                        className="btn-primary"
                        data-testid={`bulletin-read-${item.slug}`}
                    >
                        Read the briefing <ArrowRight size={16} />
                    </Link>
                </div>
            </div>
            {item.key_takeaways?.length > 0 && (
                <div className="bg-[#0D1B3E] text-[#F5F0E8] p-7">
                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                        Takeaways
                    </div>
                    <ul className="mt-4 space-y-3 text-sm leading-relaxed">
                        {item.key_takeaways.slice(0, 4).map((k, i) => (
                            <li key={i} className="flex gap-2">
                                <span className="text-[#C9A84C] mt-1">–</span>
                                <span>{k}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </article>
    );
}

function BulletinCard({ item, base = "/app/bulletins" }) {
    return (
        <Link
            to={`${base}/${item.slug}`}
            className="block card-flat p-7 flex flex-col h-full hover:border-[#C9A84C] transition-colors"
            data-testid={`bulletin-card-${item.slug}`}
        >
            <div className="flex items-center justify-between label-eyebrow">
                <span>{item.category || "Editorial"}</span>
                <span className="flex items-center gap-1 text-[#0D1B3E]/55">
                    <Clock size={11} /> {item.reading_minutes} min
                </span>
            </div>
            <h3 className="font-display text-2xl mt-3 tracking-tight leading-[1.2] hover:text-[#0D1B3E]">
                {item.title}
            </h3>
            {item.hero_excerpt && (
                <p className="mt-3 text-sm text-[#0D1B3E]/75 leading-relaxed line-clamp-4">
                    {item.hero_excerpt}
                </p>
            )}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
                {(item.related_tags || []).slice(0, 3).map((t) => (
                    <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                    >
                        #{t}
                    </span>
                ))}
            </div>
            <div className="mt-auto pt-6 flex items-center justify-between text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                <span>{formatDate(item.published_at || item.created_at)}</span>
                <span
                    className="text-[#C9A84C] flex items-center gap-1"
                    data-testid={`bulletin-read-${item.slug}`}
                >
                    Read <ArrowRight size={12} />
                </span>
            </div>
        </Link>
    );
}

function formatDate(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
}

function BulletinDetail({ slug }) {
    const { user } = useAuth();
    const base = useBulletinBase();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        setError("");
        api.get(`/bulletins/${slug}`)
            .then(({ data }) => setData(data))
            .catch((e) => {
                const d = e?.response?.data?.detail;
                setError(typeof d === "string" ? d : d?.message || "Bulletin not found.");
            })
            .finally(() => setLoading(false));
    }, [slug]);

    const b = data?.bulletin;
    const related = data?.related || [];

    useEffect(() => {
        if (!b) return;
        const prevTitle = document.title;
        const prevMeta = document.querySelector('meta[name="description"]')?.getAttribute("content");
        document.title = `${b.title} | EuroLaw Hub`;
        let metaEl = document.querySelector('meta[name="description"]');
        if (!metaEl) {
            metaEl = document.createElement("meta");
            metaEl.setAttribute("name", "description");
            document.head.appendChild(metaEl);
        }
        metaEl.setAttribute("content", b.meta_description || b.hero_excerpt || "");
        return () => {
            document.title = prevTitle;
            if (metaEl && prevMeta !== undefined && prevMeta !== null) {
                metaEl.setAttribute("content", prevMeta);
            }
        };
    }, [b]);

    if (loading) {
        return (
            <div className="container-edit py-14 flex items-center gap-3 text-[#0D1B3E]/60">
                <Loader2 className="animate-spin" size={18} />
                <span className="label-eyebrow">Loading…</span>
            </div>
        );
    }
    if (error || !b) {
        return (
            <div className="container-edit py-14" data-testid="bulletin-not-found">
                <p className="text-[#0D1B3E]/80">{error || "Not found."}</p>
                <Link to={base} className="btn-secondary mt-6">
                    <ArrowLeft size={14} /> Back to bulletins
                </Link>
            </div>
        );
    }

    return (
        <div className="container-edit py-10 lg:py-16" data-testid="bulletin-detail">
            <Link
                to={base}
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                data-testid="bulletin-back-link"
            >
                <ArrowLeft size={14} /> Bulletins
            </Link>

            <article className="max-w-3xl mt-7">
                <div className="flex items-center gap-3 label-eyebrow">
                    {b.category && <span style={{ color: "#C9A84C" }}>{b.category}</span>}
                    <span className="flex items-center gap-1">
                        <Clock size={11} /> {b.reading_minutes} min read
                    </span>
                    <span>· {formatDate(b.published_at || b.created_at)}</span>
                </div>

                <h1
                    className="font-display text-4xl lg:text-5xl mt-4 tracking-tight leading-[1.1]"
                    data-testid="bulletin-title"
                >
                    {b.title}
                </h1>

                {b.hero_excerpt && (
                    <p
                        className="mt-6 text-lg leading-relaxed text-[#0D1B3E]/85 border-l-2 border-[#C9A84C] pl-5"
                        data-testid="bulletin-hero-excerpt"
                    >
                        {b.hero_excerpt}
                    </p>
                )}

                {b.key_takeaways?.length > 0 && (
                    <aside
                        className="mt-9 bg-[#0D1B3E] text-[#F5F0E8] p-7"
                        data-testid="bulletin-takeaways"
                        aria-label="Key takeaways"
                    >
                        <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                            Key takeaways
                        </div>
                        <ul className="mt-4 space-y-3">
                            {b.key_takeaways.map((k, i) => (
                                <li key={i} className="flex gap-3 text-sm leading-relaxed">
                                    <span className="text-[#C9A84C] mt-1">–</span>
                                    <span>{k}</span>
                                </li>
                            ))}
                        </ul>
                    </aside>
                )}

                <div className="mt-10 space-y-9" data-testid="bulletin-sections">
                    {b.sections?.map((s, i) => (
                        <section key={i} aria-labelledby={`section-${i}`}>
                            <h2
                                id={`section-${i}`}
                                className="font-display text-2xl lg:text-3xl tracking-tight"
                            >
                                {s.heading}
                            </h2>
                            <div className="mt-4 space-y-4 text-[#0D1B3E]/90 leading-relaxed text-[1.05rem]">
                                {(s.body || "")
                                    .split(/\n\n+/)
                                    .filter(Boolean)
                                    .map((para, j) => (
                                        <p key={j}>{para}</p>
                                    ))}
                            </div>
                        </section>
                    ))}
                </div>

                {b.call_to_action && (
                    <aside
                        className="mt-12 border border-[#C9A84C] p-7 bg-[#FCF7E9]"
                        data-testid="bulletin-cta"
                    >
                        <div className="label-eyebrow">Next step</div>
                        <p className="mt-3 text-[#0D1B3E]/90 leading-relaxed">
                            {b.call_to_action}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-3">
                            <Link to="/app/research" className="btn-secondary">
                                Research Assistant
                            </Link>
                            <Link to="/app/compliance" className="btn-secondary">
                                Compliance Checker
                            </Link>
                            <Link to="/app/regulatory" className="btn-secondary">
                                Regulatory Intelligence
                            </Link>
                        </div>
                    </aside>
                )}

                {b.sources?.length > 0 && (
                    <section className="mt-12" data-testid="bulletin-sources">
                        <h2 className="font-display text-xl tracking-tight">Sources</h2>
                        <ul className="mt-4 space-y-2">
                            {b.sources.map((s, i) => (
                                <li key={i} className="text-sm">
                                    {s.url ? (
                                        <a
                                            href={s.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-[#0D1B3E] hover:text-[#C9A84C] inline-flex items-center gap-1.5"
                                        >
                                            {s.label} <ExternalLink size={12} />
                                        </a>
                                    ) : (
                                        <span>{s.label}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {b.related_tags?.length > 0 && (
                    <div className="mt-10 flex items-center gap-2 flex-wrap" data-testid="bulletin-tags">
                        <TagIcon size={14} className="text-[#0D1B3E]/55" />
                        {b.related_tags.map((t) => (
                            <span
                                key={t}
                                className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 border border-[#0D1B3E]/20 text-[#0D1B3E]/70"
                            >
                                #{t}
                            </span>
                        ))}
                    </div>
                )}
            </article>

            {related.length > 0 && (
                <section className="mt-16" data-testid="bulletin-related">
                    <div className="label-eyebrow">Related reading</div>
                    <h2 className="font-display text-2xl mt-2 tracking-tight">
                        More from the desk
                    </h2>
                    <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {related.map((r) => (
                            <BulletinCard key={r.id} item={r} base={base} />
                        ))}
                    </div>
                </section>
            )}

            {user && (
                <div className="mt-12">
                    <button
                        type="button"
                        onClick={async () => {
                            try {
                                setExporting(true);
                                await downloadExport(`/exports/bulletin/${b.slug}.docx`, `${b.slug}.docx`);
                            } catch (e) {
                                alert(exportError(e));
                            } finally {
                                setExporting(false);
                            }
                        }}
                        disabled={exporting}
                        className="btn-secondary"
                        data-testid="bulletin-export-docx"
                    >
                        {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                        Export to Word
                    </button>
                </div>
            )}
        </div>
    );
}

// ---- Shared export helpers ------------------------------------------------

async function downloadExport(url, filename) {
    const { api } = await import("../lib/api");
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        URL.revokeObjectURL(link.href);
        link.remove();
    }, 500);
}

function exportError(err) {
    const d = err?.response?.data;
    if (typeof d === "string") return d;
    if (d?.detail) return typeof d.detail === "string" ? d.detail : (d.detail.message || "Export failed");
    return "Export failed";
}

export { downloadExport, exportError };

function GenerateModal({ categories, defaultCategory, onClose, onComplete }) {
    const [topic, setTopic] = useState("");
    const [category, setCategory] = useState(defaultCategory || "");
    const [angle, setAngle] = useState("");
    const [stage, setStage] = useState("form"); // form | running | done | failed
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setStage("running");
        setError("");
        try {
            const { data } = await api.post("/bulletins/generate", {
                topic: topic.trim(),
                category: category || null,
                angle: angle.trim() || null,
                publish_immediately: true,
            });
            const jobId = data.job_id;
            for (let i = 0; i < 80; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                const { data: job } = await api.get(`/bulletins/jobs/${jobId}`);
                if (job.status === "ready") {
                    setStage("done");
                    onComplete(job.bulletin?.slug);
                    return;
                }
                if (job.status === "failed") {
                    setStage("failed");
                    setError(job.error || "Generation failed.");
                    return;
                }
            }
            setStage("failed");
            setError("Timed out. Your bulletin may still complete. Check back shortly.");
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setStage("failed");
            setError(typeof d === "string" ? d : d?.message || "Generation failed.");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-[#0D1B3E]/60 grid place-items-center p-4"
            onClick={stage === "running" ? undefined : onClose}
            data-testid="bulletin-generate-overlay"
        >
            <form
                onClick={(e) => e.stopPropagation()}
                onSubmit={submit}
                className="bg-white border border-[#0D1B3E]/20 w-full max-w-xl p-7"
                data-testid="bulletin-generate-modal"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="label-eyebrow">Generate</div>
                        <h2 className="font-display text-2xl mt-1 tracking-tight">
                            New bulletin
                        </h2>
                    </div>
                    {stage !== "running" && (
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-[#0D1B3E]/60 hover:text-[#0D1B3E]"
                            data-testid="bulletin-generate-close"
                        >
                            <X size={22} />
                        </button>
                    )}
                </div>

                {stage === "running" ? (
                    <div className="py-10 text-center" data-testid="bulletin-generate-running">
                        <Loader2 className="animate-spin mx-auto" size={28} color="#C9A84C" />
                        <div className="mt-4 font-display text-xl tracking-tight">
                            Drafting your bulletin…
                        </div>
                        <p className="mt-2 text-sm text-[#0D1B3E]/70">
                            The editorial desk is researching primary sources. This can
                            take 45-90 seconds.
                        </p>
                    </div>
                ) : (
                    <div className="mt-6 space-y-5">
                        <div>
                            <label className="label-eyebrow">Topic / brief</label>
                            <textarea
                                className="field mt-2"
                                rows={3}
                                placeholder="e.g. The CSDDD's value-chain due diligence and its interplay with CSRD reporting"
                                value={topic}
                                onChange={(e) => setTopic(e.target.value)}
                                required
                                data-testid="bulletin-topic-input"
                            />
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label-eyebrow">Category</label>
                                <select
                                    className="field mt-2"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    data-testid="bulletin-category-select"
                                >
                                    <option value="">Choose category…</option>
                                    {categories.map((c) => (
                                        <option key={c.key} value={c.key}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="label-eyebrow">Angle / focus (optional)</label>
                            <input
                                type="text"
                                className="field mt-2"
                                placeholder="Practitioner-focused, in-house counsel, latest case law"
                                value={angle}
                                onChange={(e) => setAngle(e.target.value)}
                                data-testid="bulletin-angle-input"
                            />
                        </div>

                        {error && (
                            <div className="text-sm text-rose-700" data-testid="bulletin-generate-error">
                                {error}
                            </div>
                        )}

                        <div className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                            Costs 3 credits
                        </div>

                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-secondary"
                                data-testid="bulletin-generate-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={!topic.trim() || topic.trim().length < 8}
                                data-testid="bulletin-generate-submit"
                            >
                                <Sparkles size={14} /> Generate
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}
